import { unwrapResult } from "@reduxjs/toolkit";
import { LoadingButton } from "@mui/lab";
import { Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import { Localized } from "@fluent/react";
import { useCallback, useState } from "react";
import { APIError, AppId, AppTextFieldHandler } from "../../../../app/appTypes";
import { useAppDispatch } from "../../../../app/hooks";
import { ServerErrorMsg } from "../../../../app/ServerErrorMsg";
import { ComputerizedSystemSelectField } from "../../../computerizedSystems/ComputerizedSystemSelectField";
import { ComputerizedSystem } from "../../../computerizedSystems/computerizedSystemsApi";
import { linkValidationProjectComputerizedSystem, ValidationProjectComputerizedSystemDto } from "../validationProjectSlice";

export interface LinkValidationProjectComputerizedSystemDialogProps {
    projectId: AppId;
    onSave: (computerizedSystem: ValidationProjectComputerizedSystemDto) => void;
    onCancel: () => void;
}

export const LinkValidationProjectComputerizedSystemDialog = ({ projectId, onSave, onCancel }: LinkValidationProjectComputerizedSystemDialogProps) => {
    const dispatch = useAppDispatch();
    const [selectedComputerizedSystemId, setSelectedComputerizedSystemId] = useState<string | null>(null);
    const [, setSelectedComputerizedSystem] = useState<ComputerizedSystem | undefined>(undefined);
    const [order, setOrder] = useState<string>("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<APIError | null>(null);

    const handleOrderChange: AppTextFieldHandler = useCallback((e) => {
        setOrder(e.target.value);
    }, []);

    const handleSaveClick = useCallback(() => {
        if (!selectedComputerizedSystemId) {
            return;
        }

        setSaving(true);
        setError(null);
        dispatch(linkValidationProjectComputerizedSystem({
            id: projectId,
            payload: {
                computerizedSystemId: Number(selectedComputerizedSystemId),
                order: order.trim() === "" ? null : Number(order),
            },
        }))
            .then(unwrapResult)
            .then((computerizedSystem) => onSave(computerizedSystem))
            .catch((err: APIError) => setError(err))
            .finally(() => setSaving(false));
    }, [dispatch, onSave, order, projectId, selectedComputerizedSystemId]);

    return <Dialog open fullWidth maxWidth="md">
        <DialogTitle>
            <Localized id="menu-computerized-systems">
                <span>Podlinkuj system skomputeryzowany</span>
            </Localized>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
            <ComputerizedSystemSelectField
                value={selectedComputerizedSystemId}
                onChange={setSelectedComputerizedSystemId}
                onSelectSystem={setSelectedComputerizedSystem}
            />
            <TextField
                sx={{ mt: 2 }}
                fullWidth
                label={<Localized id="order"><span>Kolejność</span></Localized>}
                value={order}
                onChange={handleOrderChange}
            />
            <ServerErrorMsg err={error} />
        </DialogContent>
        <DialogActions>
            <LoadingButton loading={saving} onClick={handleSaveClick} disabled={!selectedComputerizedSystemId}>
                <Localized id="save"><span>Zapisz</span></Localized>
            </LoadingButton>
            <LoadingButton loading={saving} onClick={onCancel}>
                <Localized id="cancel"><span>Anuluj</span></Localized>
            </LoadingButton>
        </DialogActions>
    </Dialog>;
};